import React, { useState, useEffect } from 'react';
import { Menu, X, Github, Linkedin, Mail, Phone, MapPin, Code, Award, Briefcase, GraduationCap, ChevronDown } from 'lucide-react';

const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('about');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['about', 'skills', 'experience', 'achievements', 'education'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const skills = [
    { name: 'React.js', icon: '⚛️' },
    { name: 'JavaScript ES6', icon: '🟨' },
    { name: 'JSX', icon: '📝' },
    { name: 'HTML5', icon: '🌐' },
    { name: 'CSS3', icon: '🎨' },
    { name: 'Redux', icon: '🔄' },
    { name: 'Git', icon: '📦' },
    { name: 'REST API', icon: '🔌' },
    { name: 'Stripe', icon: '💳' },
    { name: 'React Hooks', icon: '🎣' }
  ];

  const experiences = [
    {
      title: 'Junior Front-End React Developer',
      company: 'Gigame Info Tech Solutions',
      location: 'Madurai, India',
      period: 'Jul 2023 - Sep 2025',
      description: 'Innovative tech solutions focused on enhancing user experiences',
      highlights: [
      'Integrated multiple Buy Now Pay Later (BNPL) payment solutions including Affirm, Klarna, and Afterpay using Stripe API, increasing payment conversion rates',
        'Developed video-based FAQ system with admin management capabilities for video uploads and approval workflows, improving user onboarding experience',
                       ' Built custom calendar scheduling system for educational sessions with real-time availability tracking and session management',
                        'Implemented instructor timesheet management system tracking class sessions per candidate and managing attendance records',
                       ' Created email templates for marketing campaigns and automated email workflows',
                       ' Implemented HRMS (Human Resource Management System) processes for employee management and tracking',
                        'Integrated RESTful APIs to enhance platform functionality and user experience',
                        'Created responsive, reusable React components following component-based architecture principles',
                       ' Managed application state using Redux for complex UI interactions and data flow',
                        'Collaborated with cross-functional teams to deliver scalable frontend solutions'
      ]
    },
    {
      title: 'Front-End React Developer',
      company: 'Azure Soft Tech Solutions',
      location: 'Madurai, India',
      period: 'Jan 2023 - Jun 2023',
      description: 'Software development company specializing in frontend solutions',
      highlights: [
        'Collaborated with clients to translate business requirements',
        'Debugged and resolved complex frontend issues',
        'Implemented frontend best practices',
        'Worked cross-functionally with backend developers and QA teams'
      ]
    },
    {
      title: 'Front-End Developer Intern',
      company: 'Azure Soft Tech Solutions',
      location: 'Madurai, India',
      period: 'Jun 2022 - Dec 2022',
      highlights: [
        'Developed interactive, mobile-responsive user interfaces',
        'Refactored legacy codebase for better performance',
        'Integrated RESTful APIs for real-time data exchange',
        'Utilized Git version control for collaborative development'
      ]
    }
  ];

  const achievements = [
    { icon: '💳', title: 'Payment Gateway Integration', desc: 'Successfully integrated multiple BNPL systems, enhancing e-commerce functionality' },
    { icon: '📅', title: 'Custom Scheduling System', desc: 'Developed scheduling system improving operational efficiency for educational platforms' },
    { icon: '🧩', title: 'Reusable Component Library', desc: 'Created component library reducing development time by 40%' },
    { icon: '📧', title: 'Email Campaign System', desc: 'Designed email templates increasing engagement by 35%' },
    { icon: '👥', title: 'HRMS Implementation', desc: 'Implemented HR management processes streamlining employee operations' },
    { icon: '📊', title: 'Record Management System', desc: 'Built comprehensive candidate record management system' },
    { icon: '🎯', title: 'User Engagement Optimization', desc: 'Improved user engagement through responsive design and optimized interfaces' },
    { icon: '✅', title: '100% Project Delivery Rate', desc: 'Maintained perfect delivery rate while working in Agile environments' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-slate-950/95 backdrop-blur-lg shadow-lg shadow-blue-500/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center font-bold text-lg sm:text-xl animate-pulse">
                SS
              </div>
              <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Siva Sankar
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              {['About', 'Skills', 'Experience', 'Achievements', 'Education'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                    activeSection === item.toLowerCase()
                      ? 'text-blue-400'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item}
                  {activeSection === item.toLowerCase() && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 animate-pulse"></span>
                  )}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-800 transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-950/98 backdrop-blur-lg border-t border-slate-800 animate-in slide-in-from-top">
            <div className="px-4 py-4 space-y-2">
              {['About', 'Skills', 'Experience', 'Achievements', 'Education'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`block w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    activeSection === item.toLowerCase()
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'text-gray-300 hover:bg-slate-800'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="about" className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1 space-y-6 animate-in fade-in slide-in-from-left duration-700">
              <div className="inline-block">
                <span className="px-4 py-2 rounded-full bg-blue-500/10 text-blue-400 text-sm font-medium border border-blue-500/20">
                  Front-End Developer
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent leading-tight">
                Siva Sankar
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
                Specializing in creating responsive and scalable web applications with over 2.5 years of experience in React.js and modern frontend technologies.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://github.com/Sivasankar967"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-6 py-3 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-blue-500 transition-all hover:scale-105"
                >
                  <Github size={20} />
                  <span>GitHub</span>
                </a>
                <a
                  href="https://www.linkedin.com/in/siva-sankar-297192208/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-700 transition-all hover:scale-105"
                >
                  <Linkedin size={20} />
                  <span>LinkedIn</span>
                </a>
              </div>
              <div className="space-y-3 pt-4">
                <a href="mailto:sankarsiva6464@outlook.com" className="flex items-center space-x-3 text-gray-300 hover:text-blue-400 transition-colors">
                  <Mail size={20} className="text-blue-400" />
                  <span>sankarsiva6464@outlook.com</span>
                </a>
                <a href="tel:+916384140761" className="flex items-center space-x-3 text-gray-300 hover:text-blue-400 transition-colors">
                  <Phone size={20} className="text-blue-400" />
                  <span>+91 63841 40761</span>
                </a>
                <div className="flex items-center space-x-3 text-gray-300">
                  <MapPin size={20} className="text-blue-400" />
                  <span>Madurai, Tamil Nadu</span>
                </div>
              </div>
            </div>
         <div className="order-1 md:order-2 flex justify-center animate-in fade-in slide-in-from-right duration-700">
  <div className="relative">
    <div className="w-56 h-56 sm:w-64 sm:h-64 md:w-80 md:h-80 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 p-[2px]">
      <div className="w-full h-full rounded-full bg-slate-950 p-2">
        <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
          <img
            src="https://uploads.onecompiler.io/43c394yvb/442gehupn/siva_pic.jpeg"
            alt="Siva Sankar"
            className="rounded-full object-cover w-full h-full"
          />
        </div>
      </div>
    </div>
    <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-3xl -z-10 animate-pulse"></div>
  </div>
</div>

          </div>
          <div className="flex justify-center mt-12 animate-bounce">
            <ChevronDown size={32} className="text-blue-400" />
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <Code className="text-blue-400" size={32} />
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Technical Skills
              </h2>
            </div>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
            {skills.map((skill, index) => (
              <div
                key={skill.name}
                className="group bg-slate-900/50 backdrop-blur-sm p-6 rounded-2xl border border-slate-800 hover:border-blue-500 transition-all hover:scale-105 hover:shadow-xl hover:shadow-blue-500/20 cursor-pointer animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">{skill.icon}</div>
                <p className="text-sm sm:text-base font-medium text-center text-gray-300 group-hover:text-white transition-colors">
                  {skill.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <Briefcase className="text-blue-400" size={32} />
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Professional Experience
              </h2>
            </div>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
          </div>
          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-sm p-6 sm:p-8 rounded-2xl border border-slate-800 hover:border-blue-500 transition-all hover:shadow-xl hover:shadow-blue-500/10 animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-blue-400 mb-2">{exp.title}</h3>
                    <p className="text-lg text-purple-400 mb-1">{exp.company}</p>
                    {exp.description && (
                      <p className="text-sm text-gray-400 italic">{exp.description}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-start sm:items-end space-y-1 text-sm text-gray-400">
                    <span className="flex items-center space-x-1">
                      <MapPin size={16} />
                      <span>{exp.location}</span>
                    </span>
                    <span>{exp.period}</span>
                  </div>
                </div>
                <ul className="space-y-2 mt-4">
                  {exp.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-start space-x-2 text-gray-300">
                      <span className="text-blue-400 mt-1">▹</span>
                      <span className="flex-1">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section id="achievements" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <Award className="text-blue-400" size={32} />
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Key Achievements
              </h2>
            </div>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="bg-slate-900/50 backdrop-blur-sm p-6 rounded-2xl border border-slate-800 hover:border-blue-500 transition-all hover:scale-105 hover:shadow-xl hover:shadow-blue-500/20 animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="text-4xl mb-4">{achievement.icon}</div>
                <h3 className="text-lg font-bold text-blue-400 mb-2">{achievement.title}</h3>
                <p className="text-sm text-gray-300">{achievement.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 mb-4">
              <GraduationCap className="text-blue-400" size={32} />
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Education
              </h2>
            </div>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
          </div>
          <div className="bg-slate-900/50 backdrop-blur-sm p-8 sm:p-12 rounded-2xl border border-slate-800 hover:border-blue-500 transition-all hover:shadow-xl hover:shadow-blue-500/20 text-center animate-in fade-in zoom-in">
            <div className="text-6xl mb-6">🎓</div>
            <h3 className="text-2xl sm:text-3xl font-bold text-blue-400 mb-3">
              Bachelor of Computer Applications (B.C.A.)
            </h3>
            <p className="text-xl text-purple-400 mb-4">The American College</p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-gray-400">
              <span>June 2018 - June 2021</span>
              <span className="hidden sm:block">•</span>
              <span className="flex items-center space-x-1">
                <MapPin size={16} />
                <span>Madurai, India</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 mb-2">© 2025 Siva Sankar. All rights reserved.</p>
          <p className="text-gray-500 text-sm">Built with passion using React & Tailwind CSS</p>
        </div>
      </footer>

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        .animate-in {
          animation: fadeIn 0.6s ease-out forwards;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Portfolio;